import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Shield,
  Plus,
  Trash2,
  Globe,
  Clock,
  RefreshCw,
  Lock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Link,
  Copy,
  Key
} from 'lucide-react';

const SecuritySettingsPage = () => {
  const { api } = useAuth();
  const [whitelistedIps, setWhitelistedIps] = useState([]);
  const [defaultIp, setDefaultIp] = useState('');
  const [accessLogs, setAccessLogs] = useState([]);
  const [bypassLinks, setBypassLinks] = useState([]);
  const [universalBypass, setUniversalBypass] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBypassDialog, setShowBypassDialog] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [newIpDescription, setNewIpDescription] = useState('');
  const [newBypassDescription, setNewBypassDescription] = useState('');
  const [adding, setAdding] = useState(false);
  const [creatingBypass, setCreatingBypass] = useState(false);
  const [removingIp, setRemovingIp] = useState(null);
  const [deletingBypass, setDeletingBypass] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [ipsRes, logsRes, bypassRes] = await Promise.all([
        api.get('/security/whitelisted-ips'),
        api.get('/security/access-logs?limit=50'),
        api.get('/security/bypass-links')
      ]);
      setWhitelistedIps(ipsRes.data.whitelisted_ips || []);
      setDefaultIp(ipsRes.data.default_ip || '');
      setAccessLogs(logsRes.data.logs || []);
      setBypassLinks(bypassRes.data.bypass_links || []);
      setUniversalBypass(bypassRes.data.universal_bypass || '');
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddIp = async () => {
    if (!newIp.trim()) {
      toast.error('Please enter an IP address');
      return;
    }

    // Basic IP validation
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipPattern.test(newIp.trim())) {
      toast.error('Invalid IP address format');
      return;
    }

    setAdding(true);
    try {
      await api.post('/security/whitelisted-ips', {
        ip_address: newIp.trim(),
        description: newIpDescription.trim() || null
      });
      toast.success('IP address added to whitelist');
      setNewIp('');
      setNewIpDescription('');
      setShowAddDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add IP address');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveIp = async (ipAddress) => {
    if (ipAddress === defaultIp) {
      toast.error('Cannot remove the default whitelisted IP');
      return;
    }

    setRemovingIp(ipAddress);
    try {
      await api.delete(`/security/whitelisted-ips/${encodeURIComponent(ipAddress)}`);
      toast.success('IP address removed from whitelist');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove IP address');
    } finally {
      setRemovingIp(null);
    }
  };

  const handleCreateBypassLink = async () => {
    setCreatingBypass(true);
    try {
      const response = await api.post('/security/bypass-links', {
        description: newBypassDescription.trim() || null
      });
      toast.success('Bypass link created!');
      setNewBypassDescription('');
      setShowBypassDialog(false);
      fetchData();
      
      // Show the new link
      const fullUrl = `${window.location.origin}${response.data.full_url}`;
      navigator.clipboard.writeText(fullUrl);
      toast.info('Link copied to clipboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bypass link');
    } finally {
      setCreatingBypass(false);
    }
  };

  const handleDeleteBypassLink = async (linkId) => {
    setDeletingBypass(linkId);
    try {
      await api.delete(`/security/bypass-links/${linkId}`);
      toast.success('Bypass link deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bypass link');
    } finally {
      setDeletingBypass(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
            <Shield className="w-8 h-8 text-indigo-600" />
            Security Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage IP whitelist and access controls</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" data-testid="add-ip-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add IP Address
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Add IP to Whitelist
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-1 block">IP Address</label>
                <Input
                  placeholder="e.g., 192.168.1.100"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  data-testid="new-ip-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Description (Optional)</label>
                <Input
                  placeholder="e.g., Office WiFi, Employee Home"
                  value={newIpDescription}
                  onChange={(e) => setNewIpDescription(e.target.value)}
                />
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This IP will have immediate access to the portal. Make sure it belongs to a trusted source.
                </p>
              </div>
              
              <Button 
                onClick={handleAddIp} 
                disabled={adding}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600"
                data-testid="confirm-add-ip"
              >
                {adding ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add to Whitelist
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/20">
                <Globe className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{whitelistedIps.length + 1}</p>
                <p className="text-xs text-muted-foreground">Whitelisted IPs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{accessLogs.filter(l => l.allowed).length}</p>
                <p className="text-xs text-muted-foreground">Allowed Accesses (Recent)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg bg-gradient-to-br from-red-500/10 to-orange-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{accessLogs.filter(l => !l.allowed).length}</p>
                <p className="text-xs text-muted-foreground">Blocked Accesses (Recent)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Whitelisted IPs */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            Whitelisted IP Addresses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Default IP - Cannot be removed */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
              <div className="p-2 rounded-lg bg-indigo-500/20">
                <Globe className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-mono font-semibold">{defaultIp}</p>
                  <Badge className="bg-indigo-500">Default</Badge>
                </div>
                <p className="text-sm text-muted-foreground">Primary office IP - Cannot be removed</p>
              </div>
              <Lock className="w-5 h-5 text-indigo-400" />
            </div>

            {/* Additional Whitelisted IPs */}
            {whitelistedIps.filter(ip => ip.ip_address !== defaultIp).map((ip) => (
              <div 
                key={ip.id} 
                className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border"
                data-testid={`whitelisted-ip-${ip.ip_address}`}
              >
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-semibold">{ip.ip_address}</p>
                    <Badge variant="outline" className="text-green-600">Active</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {ip.description || 'No description'} • Added by {ip.added_by_name || ip.added_by}
                  </p>
                  <p className="text-xs text-muted-foreground/70 flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(ip.added_at)}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveIp(ip.ip_address)}
                  disabled={removingIp === ip.ip_address}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid={`remove-ip-${ip.ip_address}`}
                >
                  {removingIp === ip.ip_address ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}

            {whitelistedIps.filter(ip => ip.ip_address !== defaultIp).length === 0 && (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No additional IPs whitelisted</p>
                <p className="text-sm text-muted-foreground/70">Click "Add IP Address" to whitelist more IPs</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bypass Links - Admin Only Access */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              Admin Bypass Links
              <Badge className="bg-amber-500 ml-2">Admin Only</Badge>
            </CardTitle>
            <Dialog open={showBypassDialog} onOpenChange={setShowBypassDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Generate Link
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary" />
                    Generate Bypass Link
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Description (Optional)</label>
                    <Input
                      placeholder="e.g., Remote admin access, Emergency backup"
                      value={newBypassDescription}
                      onChange={(e) => setNewBypassDescription(e.target.value)}
                    />
                  </div>
                  
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      <strong>Important:</strong> Bypass links only allow <strong>ADMIN</strong> users to login. 
                      Executives and managers cannot use these links.
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleCreateBypassLink} 
                    disabled={creatingBypass}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
                  >
                    {creatingBypass ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Generate Bypass Link
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Universal Bypass */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Lock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-mono font-semibold">{universalBypass}</p>
                  <Badge className="bg-amber-500">Universal</Badge>
                </div>
                <p className="text-sm text-muted-foreground">Permanent bypass link - Cannot be deleted</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(`${window.location.origin}${universalBypass}`)}
                className="gap-1"
              >
                <Copy className="w-3 h-3" />
                Copy
              </Button>
            </div>

            {/* Generated Bypass Links */}
            {bypassLinks.map((link) => (
              <div 
                key={link.id} 
                className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border"
              >
                <div className="p-2 rounded-lg bg-indigo-500/20">
                  <Link className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm truncate">/bypass/{link.token.slice(0, 20)}...</p>
                    <Badge variant="outline" className="text-indigo-600">Active</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {link.description || 'No description'} • Used {link.usage_count || 0} times
                  </p>
                  <p className="text-xs text-muted-foreground/70 flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    Created: {formatDate(link.created_at)}
                    {link.last_used_at && ` • Last used: ${formatDate(link.last_used_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`${window.location.origin}/bypass/${link.token}`)}
                    className="gap-1"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteBypassLink(link.id)}
                    disabled={deletingBypass === link.id}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    {deletingBypass === link.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}

            {bypassLinks.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <p>No custom bypass links generated</p>
                <p className="text-sm text-muted-foreground/70">Click "Generate Link" to create one</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Access Logs */}
      <Card className="border-0 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Recent Access Logs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {accessLogs.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No access logs yet</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accessLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {log.allowed ? (
                          <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Allowed</Badge>
                        ) : (
                          <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Blocked</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{log.ip_address}</TableCell>
                      <TableCell className="text-sm truncate max-w-[200px]">{log.path}</TableCell>
                      <TableCell className="text-sm">{formatDate(log.timestamp)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{log.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettingsPage;
